// The colour check (docs/ROADMAP.md D34, docs/ART-DIRECTION.md §3): no two kinds of thing the game draws may share a
// colour, for normal vision and for the three colour vision deficiencies. Each deficiency is simulated with the
// matrices of Machado, Oliveira & Fernandes (2009, severity 1) on linear RGB, and colours are compared in OKLab (ΔE ×100:
// about 2 is a just-noticeable difference side by side; thin lines need far more). COLOUR_RULES says which roles must
// stay how far apart; the tuner lists every broken rule while colours are picked, and shows each vision through the
// same matrices.

import { srgbChannels, type ColourHex } from './colour'
import { hasColourBlindOverride, paletteColours, PALETTE_MODES, type ColourRole, type PaletteMode } from './palette'
import type { TuningValues } from './values'

export const VISIONS = ['normal', 'protan', 'deutan', 'tritan'] as const
export type Vision = (typeof VISIONS)[number]

type Row3 = readonly [number, number, number]
export type Matrix3 = readonly [Row3, Row3, Row3]

/** Linear RGB as each vision sees it: normal vision unchanged; each dichromacy by Machado et al. 2009, severity 1.0. */
export const VISION_MATRICES: Readonly<Record<Vision, Matrix3>> = {
  normal: [
    [1, 0, 0],
    [0, 1, 0],
    [0, 0, 1],
  ],
  protan: [
    [0.152286, 1.052583, -0.204868],
    [0.114503, 0.786281, 0.099216],
    [-0.003882, -0.048116, 1.051998],
  ],
  deutan: [
    [0.367322, 0.860646, -0.227968],
    [0.280085, 0.672501, 0.047413],
    [-0.01182, 0.04294, 0.968881],
  ],
  tritan: [
    [1.255528, -0.076749, -0.178779],
    [-0.078411, 0.930809, 0.147602],
    [0.004733, 0.691367, 0.3039],
  ],
}

const SRGB_LINEAR_KNEE = 0.04045
const SRGB_LINEAR_SLOPE = 12.92
const SRGB_OFFSET = 0.055
const SRGB_GAMMA = 2.4
/** OKLab differences are reported ×100. */
const DELTA_E_SCALE = 100

function linearChannel(channel: number): number {
  return channel <= SRGB_LINEAR_KNEE ? channel / SRGB_LINEAR_SLOPE : ((channel + SRGB_OFFSET) / (1 + SRGB_OFFSET)) ** SRGB_GAMMA
}

function clampUnit(value: number): number {
  return Math.min(1, Math.max(0, value))
}

type Triple = readonly [number, number, number]

function applyRow(row: Row3, r: number, g: number, b: number): number {
  return clampUnit(row[0] * r + row[1] * g + row[2] * b)
}

/** The colour's linear RGB as `vision` sees it. */
function seenLinear(colour: ColourHex, vision: Vision): Triple {
  const [sr, sg, sb] = srgbChannels(colour)
  const r = linearChannel(sr)
  const g = linearChannel(sg)
  const b = linearChannel(sb)
  const m = VISION_MATRICES[vision]
  return [applyRow(m[0], r, g, b), applyRow(m[1], r, g, b), applyRow(m[2], r, g, b)]
}

/** OKLab (Ottosson 2020) of linear sRGB. */
function oklab([r, g, b]: Triple): Triple {
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b)
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b)
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b)
  return [0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s, 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s, 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s]
}

/** How far apart two colours look to `vision`: ΔE in OKLab, ×100. */
export function colourDistance(a: ColourHex, b: ColourHex, vision: Vision): number {
  const p = oklab(seenLinear(a, vision))
  const q = oklab(seenLinear(b, vision))
  return DELTA_E_SCALE * Math.hypot(p[0] - q[0], p[1] - q[1], p[2] - q[2])
}

/** The least distance a pair must keep for normal vision and for each deficiency. */
interface Apart {
  readonly normal: number
  readonly deficient: number
}

/** How far apart pairs must stay, by how badly mixing them up hurts. */
const APART = {
  /** Whose is this city, line, unit? */
  factions: { normal: 25, deficient: 18 },
  /** A faction's lines and rings against the map's coastlines, borders and land dots. */
  factionMap: { normal: 18, deficient: 12 },
  /** A faction against the faint grid and the atmosphere. */
  factionAmbient: { normal: 12, deficient: 8 },
  /** Coastlines against borders. */
  mapLines: { normal: 18, deficient: 10 },
  /** The map's lines against its land dots (dots and lines also differ in shape). */
  mapLinesDots: { normal: 12, deficient: 6 },
  /** The faint grid and the atmosphere against the rest of the map. */
  ambient: { normal: 8, deficient: 4 },
  /** Money, warning and danger against each other. */
  signals: { normal: 18, deficient: 10 },
  /** A HUD signal against a faction colour. */
  signalFaction: { normal: 10, deficient: 5 },
  /** Effect sparks against the factions they fly over. */
  sparkFaction: { normal: 10, deficient: 5 },
  /** Sparks against the warm colours they sit among (they also differ in motion and life). */
  sparkWarm: { normal: 5, deficient: 3 },
} as const satisfies Readonly<Record<string, Apart>>

export interface ColourRule {
  readonly a: ColourRole
  readonly b: ColourRole
  readonly apart: Apart
}

const FACTIONS = ['player', 'enemy', 'neutral'] as const satisfies readonly ColourRole[]
const MAP_LINES = ['coastline', 'borders'] as const satisfies readonly ColourRole[]
const AMBIENT = ['grid', 'atmosphere'] as const satisfies readonly ColourRole[]
const SIGNALS = ['money', 'warning', 'danger'] as const satisfies readonly ColourRole[]

function everyPair(roles: readonly ColourRole[], apart: Apart): ColourRule[] {
  const rules: ColourRule[] = []
  for (let i = 0; i < roles.length; i++) for (let j = i + 1; j < roles.length; j++) rules.push({ a: roles[i] as ColourRole, b: roles[j] as ColourRole, apart })
  return rules
}

function across(left: readonly ColourRole[], right: readonly ColourRole[], apart: Apart): ColourRule[] {
  return left.flatMap((a) => right.map((b) => ({ a, b, apart })))
}

/** Which roles must look apart (roles not listed may match: text and frames, say, or space and the globe body). */
export const COLOUR_RULES: readonly ColourRule[] = [
  ...everyPair(FACTIONS, APART.factions),
  ...across(FACTIONS, [...MAP_LINES, 'landDots'], APART.factionMap),
  ...across(FACTIONS, AMBIENT, APART.factionAmbient),
  ...everyPair(MAP_LINES, APART.mapLines),
  ...across(MAP_LINES, ['landDots'], APART.mapLinesDots),
  ...across(AMBIENT, [...MAP_LINES, 'landDots'], APART.ambient),
  ...everyPair(AMBIENT, APART.ambient),
  ...everyPair(SIGNALS, APART.signals),
  ...across(SIGNALS, FACTIONS, APART.signalFaction),
  ...across(['spark'], ['player', 'neutral'], APART.sparkFaction),
  ...across(['spark'], ['enemy', 'danger', 'warning'], APART.sparkWarm),
]

export interface ColourPairReading {
  readonly rule: ColourRule
  readonly mode: PaletteMode
  readonly vision: Vision
  readonly distance: number
  /** The least distance this vision must see. */
  readonly floor: number
}

/** The colour-blind palette is judged only on rules it changes (the others read the same colours as the normal one). */
const JUDGED: Readonly<Record<PaletteMode, (rule: ColourRule) => boolean>> = {
  normal: () => true,
  colourBlind: (rule) => hasColourBlindOverride(rule.a) || hasColourBlindOverride(rule.b),
}

/** Every judged pair, in both palettes and every vision. */
export function colourPairReadings(values: TuningValues): ColourPairReading[] {
  const readings: ColourPairReading[] = []
  for (const mode of PALETTE_MODES) {
    const colours = paletteColours(values, mode)
    for (const rule of COLOUR_RULES) {
      if (!JUDGED[mode](rule)) continue
      for (const vision of VISIONS) {
        const floor = vision === 'normal' ? rule.apart.normal : rule.apart.deficient
        readings.push({ rule, mode, vision, distance: colourDistance(colours[rule.a], colours[rule.b], vision), floor })
      }
    }
  }
  return readings
}

/** How close a reading is to breaking its rule: below 1 breaks it. */
export function margin(reading: ColourPairReading): number {
  return reading.distance / reading.floor
}

/** Every broken rule, the worst first. */
export function colourClashes(values: TuningValues): ColourPairReading[] {
  return colourPairReadings(values)
    .filter((reading) => margin(reading) < 1)
    .sort((x, y) => margin(x) - margin(y))
}

/** Per palette and vision, the pair closest to breaking its rule. */
export function tightestPairs(values: TuningValues): ColourPairReading[] {
  const tightest = new Map<string, ColourPairReading>()
  for (const reading of colourPairReadings(values)) {
    const key = `${reading.mode}/${reading.vision}`
    const held = tightest.get(key)
    if (held === undefined || margin(reading) < margin(held)) tightest.set(key, reading)
  }
  return [...tightest.values()]
}
